import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { getSessionFlow, getLandingPageDistribution } from "../../../../util/analyticsApi";

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SelectLabel = styled.label`
  font-weight: bold;
  font-size: 14px;
`;

const PathSelect = styled.select`
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  min-width: 250px;

  @media (max-width: 768px) {
    min-width: unset;
    width: 100%;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;

const HeaderCell = styled.th`
  padding: 8px 6px;
  text-align: center;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #ddd;
  font-size: 11px;
  white-space: nowrap;
`;

const RowHeader = styled.th`
  padding: 8px 10px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  white-space: nowrap;
`;

const DataCell = styled.td`
  padding: 6px;
  text-align: center;
  border-bottom: 1px solid #eee;
  position: relative;
`;

const CellContent = styled.div`
  position: relative;
  z-index: 1;
  font-weight: ${(props) => (props.$intensity > 30 ? "600" : "400")};
  color: ${(props) => (props.$intensity > 50 ? "white" : "#333")};
  font-size: 12px;
`;

const CellBg = styled.div`
  position: absolute;
  inset: 2px;
  border-radius: 3px;
  background: ${(props) =>
    props.$isExit
      ? `rgba(244, 67, 54, ${props.$opacity})`
      : `rgba(76, 175, 80, ${props.$opacity})`};
`;

const TotalCell = styled.td`
  padding: 6px 10px;
  text-align: center;
  border-bottom: 1px solid #eee;
  color: #888;
  font-size: 11px;
`;

const Legend = styled.div`
  margin-top: 12px;
  font-size: 11px;
  color: #999;
`;

const SessionFlowTable = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paths, setPaths] = useState([]);
  const [selectedPath, setSelectedPath] = useState("");
  const [pathsLoading, setPathsLoading] = useState(true);

  useEffect(function fetchLandingPathsWhenFiltersChange() {
    const loadPaths = async () => {
      setPathsLoading(true);
      try {
        const result = await getLandingPageDistribution(filters);
        const allPaths = [];
        result.forEach((item) => {
          allPaths.push({ path: item.path, count: item.count });
        });
        setPaths(allPaths);
      } catch (err) {
        console.error("Error loading paths:", err);
        setPaths([]);
      } finally {
        setPathsLoading(false);
      }
    };
    loadPaths();
  }, [filters]);

  useEffect(function fetchSessionFlowData() {
    const load = async () => {
      setLoading(true);
      try {
        const params = { ...filters };
        if (selectedPath) params.landingPath = selectedPath;
        const result = await getSessionFlow(params);
        setData(result);
      } catch (err) {
        console.error("Error loading session flow:", err);
        setData({ actions: [], transitions: {} });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filters, selectedPath]);

  useEffect(function resetSelectedPathWhenFiltersChange() {
    setSelectedPath("");
  }, [filters]);

  if (loading) return <LoadingMessage>Loading session flow data...</LoadingMessage>;

  const hasData = data && data.actions.length > 0;
  const columns = hasData ? [...data.actions, "Exited"] : [];

  return (
    <Container>
      <FilterRow>
        <SelectLabel>Filter by landing page:</SelectLabel>
        <PathSelect
          value={selectedPath}
          onChange={(e) => setSelectedPath(e.target.value)}
          disabled={pathsLoading}
        >
          <option value="">All landing pages</option>
          {paths.map((p) => (
            <option key={p.path} value={p.path}>
              {p.path} ({p.count.toLocaleString()})
            </option>
          ))}
        </PathSelect>
      </FilterRow>

      {!hasData ? (
        <NoDataMessage>
          No session flow data available yet. This requires sessionId tracking on analytics events.
        </NoDataMessage>
      ) : (
        <>
          <div style={{ overflowX: "auto" }}>
            <Table>
              <thead>
                <tr>
                  <HeaderCell style={{ textAlign: "left" }}>From \ To</HeaderCell>
                  {columns.map((col) => (
                    <HeaderCell key={col}>{col}</HeaderCell>
                  ))}
                  <HeaderCell style={{ color: "#aaa" }}>Total</HeaderCell>
                </tr>
              </thead>
              <tbody>
                {data.actions.map((from) => {
                  const row = data.transitions[from] || {};
                  return (
                    <tr key={from}>
                      <RowHeader>{from}</RowHeader>
                      {columns.map((to) => {
                        const cell = row[to] || { count: 0, percent: 0 };
                        const isExit = to === "Exited";
                        const opacity = Math.min(cell.percent / 100, 0.85);
                        return (
                          <DataCell key={to}>
                            {cell.percent > 0 && (
                              <CellBg $isExit={isExit} $opacity={opacity} />
                            )}
                            <CellContent $intensity={cell.percent}>
                              {cell.percent > 0
                                ? `${cell.percent}%`
                                : "\u2014"}
                            </CellContent>
                          </DataCell>
                        );
                      })}
                      <TotalCell>{(row._total || 0).toLocaleString()}</TotalCell>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          <Legend>
            Each row shows: after doing action X, what % of the time did users do action Y next?
            Green = continued engaging. Red = exited (session ended).
          </Legend>
        </>
      )}
    </Container>
  );
};

export default SessionFlowTable;
